import { Component, OnInit, Inject } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormArray,
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
} from '@angular/forms';

import { ModalData, ModalRef } from '@purespectrum1/ui/modal';
import {
  QuestionMapping,
  ProcessedTarget,
  MappingModalData,
  BuyerQualifications,
  ProcessedTargetMapping,
  RangeSet,
  QualificationData,
} from '../../../shared/interfaces/qualification-mappings.interface';
import { Children, Child } from 'purespectrum-lib';
import { notifyMessage } from '../../../constants/notify-message';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import {
  QUALIFICATION_CODES,
  QUALIFICATION_TYPES,
  CHILD_AGE_GENDER_CONSTANTS,
} from '../../../constants/qualification-code';
import { Constants as CreateSurveyConstants } from '../../../create-surveys/create-survey.constants';
import { DEFAULT_AGE_QUOTA_CRITERIA } from '../../../constants/default-age-genpop-quota-criteria';
import { DecipherImportHelperService } from '../../../shared/services/decipher/decipher-import-helper/decipher-import-helper.service';

@Component({
  selector: 'ps-mapping-modal',
  templateUrl: './mapping-modal.component.html',
  styleUrls: ['./mapping-modal.component.css'],
})
export class MappingModalComponent implements OnInit {
  public customQuotasState: {
    [key: number]: {
      show: boolean;
      gender: 'boy' | 'girl' | 'either' | null;
      from: number | null;
      to: number | null;
    };
  } = {};

  public qualificationTypes = QUALIFICATION_TYPES;
  public qualificationCodes = QUALIFICATION_CODES;
  private _minRange!: number;
  private _maxRange!: number;
  private _emptyList: [] = [];
  private _generateModalForm = () => {
    return this._fb.group({
      target: this._fb.array([]),
    });
  };

  private _generateTargetForm = (targetData?: ProcessedTarget) => {
    const {
      decipherCode = '',
      desc = '',
      mappings = [],
      decipherCondition = '',
    } = targetData || {};
    const targetForm = this._fb.group({
      decipherCode: [decipherCode],
      desc: [desc],
      decipherCondition: [decipherCondition],
      mappings: this._fb.array([]),
    });
    if (mappings.length) {
      mappings.forEach((mapping: ProcessedTargetMapping) => {
        (targetForm.get('mappings')! as UntypedFormArray).push(
          this._generateMappingsForm(mapping)
        );
      });
    }
    return targetForm;
  };

  private _generateMappingsForm = (targetMapping?: ProcessedTargetMapping) => {
    const {
      qualificationCode = null,
      conditionCodes = [],
      rangeSets,
    } = targetMapping || {};
    const formObj: { [key in keyof ProcessedTargetMapping]: any } = {
      qualificationCode: [qualificationCode],
      conditionCodes: this._fb.array([...conditionCodes]),
    };
    if (rangeSets) {
      delete formObj.conditionCodes;
      formObj.rangeSets = this._fb.group({
        from: [rangeSets.from],
        to: [rangeSets.to],
      });
      if (
        !isNaN(
          this.qualificationDetails[0].localizations[0].format?.min as number
        ) &&
        !isNaN(
          this.qualificationDetails[0].localizations[0].format?.max as number
        )
      ) {
        this._minRange = this.qualificationDetails[0].localizations[0].format
          .min as number;
        this._maxRange = this.qualificationDetails[0].localizations[0].format
          .max as number;
        formObj.rangeSets.controls.from.setValidators([
          Validators.min(this._minRange),
          Validators.max(this._maxRange),
        ]);
        formObj.rangeSets.controls.to.setValidators([
          Validators.min(this._minRange),
          Validators.max(this._maxRange),
        ]);
      }
    }
    return this._fb.group(formObj);
  };

  private _generateConditionControl = (conditionCode?: number) => {
    return new UntypedFormControl(conditionCode);
  };

  modalForm = this._generateModalForm();

  constructor(
    @Inject(ModalData) private _modalData: MappingModalData,
    private _fb: UntypedFormBuilder,
    private _toastr: ToasterService,
    private _modalRef: ModalRef<MappingModalComponent, ProcessedTarget[]>,
    private _decipherImportHelper: DecipherImportHelperService
  ) {}

  public mappingData: QuestionMapping = this._modalData.mappingData;
  public isUsedInDefaultMapping: boolean =
    this._modalData.isUsedInDefaultMapping;
  public qualificationDetails: BuyerQualifications[] =
    this._modalData.qualificationDetails;

  public genderOptions = Object.entries(
    CreateSurveyConstants.CHILD_GENDER_MAPPING
  ).map(([value, label]) => ({ value, label }));

  public toggleCustomQuotas = (index: number) => {
    if (!this.customQuotasState[index]) {
      this.customQuotasState[index] = {
        show: true,
        gender: null,
        from: null,
        to: null,
      };
    } else {
      this.customQuotasState[index].show = !this.customQuotasState[index].show;
    }
  };

  public onGenderChange = (
    index: number,
    mapping: UntypedFormGroup,
    event: Event
  ) => {
    if (!this.customQuotasState[index]) {
      this.customQuotasState[index] = {
        show: true,
        gender: (event.target as HTMLSelectElement).value as
          | 'boy'
          | 'girl'
          | 'either',
        from: null,
        to: null,
      };
    } else {
      this.customQuotasState[index].gender = (event.target as HTMLSelectElement)
        .value as 'boy' | 'girl' | 'either';
    }
    const { gender, from, to } = this.customQuotasState[index];
    if (gender !== null && from !== null && to !== null) {
      this.autoSelectConditionCodes(mapping, index, gender, from, to);
    }
  };

  public onFromChange = (
    index: number,
    mapping: UntypedFormGroup,
    event: Event
  ) => {
    const value = (event.target as HTMLInputElement).value;
    if (!this.customQuotasState[index]) {
      this.customQuotasState[index] = {
        show: true,
        gender: null,
        from: value ? parseInt(value, 10) : null,
        to: null,
      };
    } else {
      this.customQuotasState[index].from = value ? parseInt(value, 10) : null;
    }
    const { gender, from, to } = this.customQuotasState[index];
    if (gender !== null && from !== null && to !== null) {
      this.autoSelectConditionCodes(mapping, index, gender, from, to);
    }
  };

  public onToChange = (
    index: number,
    mapping: UntypedFormGroup,
    event: Event
  ) => {
    const value = (event.target as HTMLInputElement).value;
    if (!this.customQuotasState[index]) {
      this.customQuotasState[index] = {
        show: true,
        gender: null,
        from: null,
        to: value ? parseInt(value, 10) : null,
      };
    } else {
      this.customQuotasState[index].to = value ? parseInt(value, 10) : null;
    }
    const { gender, from, to } = this.customQuotasState[index];
    if (gender !== null && from !== null && to !== null) {
      this.autoSelectConditionCodes(mapping, index, gender, from, to);
    }
  };

  public isCustomQuotasShown = (index: number): boolean => {
    return !!this.customQuotasState[index]?.show;
  };

  public getCustomQuotasState = (index: number) => {
    if (!this.customQuotasState[index]) {
      this.customQuotasState[index] = {
        show: false,
        gender: null,
        from: null,
        to: null,
      };
    }
    return this.customQuotasState[index];
  };

  public getGenderLabel = (genderValue: string | null): string => {
    const option = this.genderOptions.find((opt) => opt.value === genderValue);
    return option ? option.label : 'Select Gender';
  };

  public onGenderSelect = (
    index: number,
    mapping: UntypedFormGroup,
    selectedItem: any
  ) => {
    if (!this.customQuotasState[index]) {
      this.customQuotasState[index] = {
        show: true,
        gender: selectedItem.value,
        from: null,
        to: null,
      };
    } else {
      this.customQuotasState[index].gender = selectedItem.value;
    }
    const { gender, from, to } = this.customQuotasState[index];
    if (gender !== null && from !== null && to !== null) {
      this.autoSelectConditionCodes(mapping, index, gender, from, to);
    }
  };

  public autoSelectConditionCodes = (
    mapping: UntypedFormGroup,
    targetIndex: number,
    gender: string,
    fromAge: any,
    toAge: any
  ) => {
    const conditionCodes = mapping.get('conditionCodes') as UntypedFormArray;

    // Clear existing condition codes before pushing new ones
    conditionCodes.clear();

    const children = Children.create(false, gender, fromAge, toAge);
    const condition_codes = children
      .toEachChild()
      .map(({ code }) => code.toString());
    condition_codes.forEach((code) => {
      conditionCodes.push(this._generateConditionControl(parseInt(code)));
    });
  };

  public isChildAgeGenderMapping: boolean = this.qualificationDetails?.some(
    (q) => q.qualification_code === this.qualificationCodes.CHILD_AGE_GENDER
  );

  private _isCustomQuotaStateIncomplete = (
    gender: string | null,
    from: number | null,
    to: number | null
  ): boolean => {
    return !gender || from === null || to === null;
  };

  private _isCustomQuotaAgeOutOfRange = (from: number, to: number): boolean => {
    return (
      from < 0 ||
      from > 17 ||
      to < 0 ||
      to > 17 ||
      from > to ||
      (from === 0 && to === 0)
    );
  };

  private _isCustomQuotasValid = (): boolean => {
    if (!this.isChildAgeGenderMapping) {
      return true;
    }
    for (const index of Object.keys(this.customQuotasState).map(Number)) {
      const state = this.customQuotasState[index];
      if (!state.show) {
        continue;
      }
      const { gender, from, to } = state;
      if (!gender && from === null && to === null) {
        continue;
      }
      if (this._isCustomQuotaStateIncomplete(gender, from, to)) {
        this._toastr.error(
          notifyMessage.errorMessage.CONDITION_MAPPING_ERROR
            .CUSTOM_QUOTA_INCOMPLETE
        );
        return false;
      }
      if (this._isCustomQuotaAgeOutOfRange(from!, to!)) {
        this._toastr.error(
          notifyMessage.errorMessage.CONDITION_MAPPING_ERROR
            .CUSTOM_QUOTA_AGE_RANGE
        );
        return false;
      }
    }
    return true;
  };

  private _isInvalidMonthYearMix = (codes: number[]): boolean => {
    const ages = codes
      .map((code) => {
        const { age, isBaby } = new Child(code);
        return { age: isBaby ? age : age * 12, isBaby };
      })
      .sort((a, b) => a.age - b.age);
    const minAge = ages[0];
    const maxAge = ages[ages.length - 1];
    return (
      minAge.isBaby && minAge.age > 11 && !maxAge.isBaby && maxAge.age / 12 > 3
    );
  };

  private _hasInvalidCAGCodeCombination = (): boolean => {
    if (!this.isChildAgeGenderMapping) {
      return false;
    }
    const targets: ProcessedTarget[] = this.modalForm.value.target;
    for (let i = 0; i < targets.length; i++) {
      if (this.customQuotasState[i]?.show) {
        continue;
      }
      const codes = this._getConditionCodesForTarget(targets[i]);
      if (codes.length && this._isInvalidMonthYearMix(codes)) {
        return true;
      }
    }
    return false;
  };

  private _getConditionCodesForTarget = (target: ProcessedTarget): number[] => {
    const mapping = target.mappings.find(
      (m) => m.qualificationCode === this.qualificationCodes.CHILD_AGE_GENDER
    );
    return (mapping?.conditionCodes ?? [])
      .filter(Boolean)
      .map(Number)
      .filter((n) => n !== CHILD_AGE_GENDER_CONSTANTS.NO_CHILDREN_CODE);
  };

  private _getCAGUnit = (codes: number[]): number => {
    const allBaby = codes.map((c) => new Child(c)).every((c) => c.isBaby);
    return allBaby
      ? CHILD_AGE_GENDER_CONSTANTS.MONTHS_UNIT
      : CHILD_AGE_GENDER_CONSTANTS.YEARS_UNIT;
  };

  private _deriveAgeGenderRange = (
    codes: number[],
    unit: number
  ): { from: number; to: number; gender: string } => {
    const cag = new Children(
      codes.map((c) => new Child(c)),
      unit
    );
    return { from: cag.age.from, to: cag.age.to, gender: cag.gender };
  };

  private _overlapsWithExisting = (
    incoming: { from: number; to: number; gender: string },
    seen: Array<{ from: number; to: number; gender: string }>
  ): boolean => {
    return seen.some(
      (existing) =>
        incoming.gender === existing.gender &&
        incoming.to > existing.from &&
        incoming.from < existing.to
    );
  };

  private _hasOverlappingChildConditionCodes = (): boolean => {
    if (!this.isChildAgeGenderMapping) {
      return false;
    }
    const seen: Array<{ from: number; to: number; gender: string }> = [];
    const targets = this.modalForm.value.target as ProcessedTarget[];
    for (let i = 0; i < targets.length; i++) {
      const codes = this._getConditionCodesForTarget(targets[i]);
      if (!codes.length) {
        continue;
      }
      const isCustomRow = !!this.customQuotasState[i]?.show;
      const unit = isCustomRow
        ? CHILD_AGE_GENDER_CONSTANTS.YEARS_UNIT
        : this._getCAGUnit(codes);
      const range = this._deriveAgeGenderRange(codes, unit);
      if (this._overlapsWithExisting(range, seen)) {
        return true;
      }
      seen.push(range);
    }
    return false;
  };

  public close = () => {
    if (!this._isCustomQuotasValid()) {
      return;
    }
    if (this._hasInvalidCAGCodeCombination()) {
      this._toastr.error(
        notifyMessage.errorMessage.CONDITION_MAPPING_ERROR
          .INVALID_CAG_CODE_COMBINATION
      );
      return;
    }
    if (this.modalForm.status === 'INVALID') {
      if (
        this.isRangeType(this.qualificationDetails[0].qualification_code) &&
        !isNaN(this._minRange) &&
        !isNaN(this._maxRange)
      ) {
        if (this._isOutOfRange()) {
          return;
        }
      }
      this._toastr.error(
        notifyMessage.errorMessage.CONDITION_MAPPING_ERROR.INVALID_MAPPING
      );
      return;
    }
    if (this._areOptionsMapped()) {
      const hasDuplicate = this.hasDuplicateMapping(
        this.modalForm.value.target
      );
      if (hasDuplicate) {
        this._toastr.error(
          notifyMessage.errorMessage.CONDITION_MAPPING_ERROR.DUPLICATE_MAPPING
        );
        return;
      }
      if (this._hasOverlappingChildConditionCodes()) {
        this._toastr.error(
          notifyMessage.errorMessage.CONDITION_MAPPING_ERROR
            .OVERLAPPING_CONDITION_CODES
        );
        return;
      }
      if (this._isDefaultMappingError()) {
        return;
      }
      this._modalRef.close(this.modalForm.get('target')!.value);
    }
  };

  private _areOptionsMapped = () => {
    return (
      (this.isUsedInDefaultMapping && this._hasAllMappings()) ||
      (!this.isUsedInDefaultMapping &&
        this._hasAtleastOneMappingCheck() &&
        this._hasNoPartialMappingCheck())
    );
  };

  public hasDuplicateMapping = (targets: ProcessedTarget[]) => {
    return this._decipherImportHelper.hasDuplicate(targets);
  };

  private _isOutOfRange = () => {
    let isErrorShown = false;
    (this.modalForm.controls.target as UntypedFormArray).controls.forEach(
      (target: any) => {
        (target.controls.mappings as UntypedFormArray).controls.forEach(
          (mapping: any) => {
            if (
              mapping.controls.rangeSets.controls.from.errors?.min ||
              mapping.controls.rangeSets.controls.from.errors?.max ||
              mapping.controls.rangeSets.controls.to.errors?.min ||
              mapping.controls.rangeSets.controls.to.errors?.max
            ) {
              this._toastr.error(
                'Enter ' +
                  this.qualificationDetails[0].desc +
                  ' between ' +
                  this._minRange +
                  ' to ' +
                  this._maxRange
              );
              isErrorShown = true;
              return;
            }
          }
        );
      }
    );
    return isErrorShown;
  };

  private _isDefaultMappingError = () => {
    if (this.isUsedInDefaultMapping) {
      let isErrorShown = false;
      this.modalForm.value.target.forEach((target: any) => {
        if (target.mappings && target.mappings.length) {
          target.mappings.forEach((mapping: any) => {
            const targetMappingCheck =
              this.isRangeType(
                this.qualificationDetails[0].qualification_code
              ) ||
              (target.decipherCondition &&
                mapping.conditionCodes &&
                mapping.conditionCodes.length &&
                !isNaN(parseInt(mapping.conditionCodes[0])));
            if (targetMappingCheck) {
              isErrorShown = false;
            } else {
              this._toastr.error(
                notifyMessage.errorMessage.CONDITION_MAPPING_ERROR
                  .INVALID_MAPPING
              );
              isErrorShown = true;
              return;
            }
          });
        }
      });
      return isErrorShown;
    }
    return false;
  };

  private _hasAtleastOneMappingCheck = () => {
    const targetObjs = this.modalForm.value.target;
    const hasAtleastOneMapping =
      this._decipherImportHelper.hasAtleastOneMapping(
        targetObjs,
        this.qualificationDetails
      );
    if (!hasAtleastOneMapping) {
      this._toastr.error(
        notifyMessage.errorMessage.CONDITION_MAPPING_ERROR.ATLEAST_ONE_MAPPING
      );
      return false;
    }
    return true;
  };

  private _hasNoPartialMappingCheck = () => {
    const targetObjs = this.modalForm.value.target;
    const hasNoPartialMapping = this._decipherImportHelper.hasNoPartialMapping(
      targetObjs,
      this.qualificationDetails
    );
    if (!hasNoPartialMapping) {
      this._toastr.error(
        notifyMessage.errorMessage.CONDITION_MAPPING_ERROR.PARTIAL_MAPPING
      );
      return false;
    }
    return true;
  };

  private _hasAllMappings = () => {
    const hasAllMappings = this._decipherImportHelper.hasAllMappings(
      this.modalForm.value.target,
      this.qualificationDetails
    );
    if (!hasAllMappings) {
      this._toastr.error(
        notifyMessage.errorMessage.CONDITION_MAPPING_ERROR.MISSING_MAPPING
      );
      return false;
    }
    return true;
  };

  public getQualificationDetail = (qCode: number) => {
    return this.qualificationDetails.find(
      (q) => q.qualification_code === qCode
    );
  };

  public getQualificationAnswers = (qCode: number) => {
    const qual = this.getQualificationDetail(qCode);
    return qual && qual.localizations && qual.localizations.length
      ? qual.localizations[0].answers
      : this._emptyList;
  };

  public getTargetFormControls = () => {
    return (this.modalForm.get('target') as UntypedFormArray)
      .controls as UntypedFormGroup[];
  };

  public getMappingFormControls = (target: UntypedFormGroup) => {
    return (target.get('mappings') as UntypedFormArray)
      .controls as UntypedFormGroup[];
  };

  public getConditionControls = (
    mappingFG: UntypedFormGroup
  ): UntypedFormControl[] => {
    return (mappingFG.get('conditionCodes') as UntypedFormArray)
      .controls as UntypedFormControl[];
  };

  public addCondition = (targetMapping: UntypedFormGroup) => {
    (targetMapping.get('conditionCodes') as UntypedFormArray).push(
      this._generateConditionControl()
    );
  };

  public addConditionMapping = (qualificationCode: number, index?: number) => {
    let mappings;
    if (this.isRangeType(this.qualificationDetails[0].qualification_code)) {
      mappings = [
        {
          qualificationCode,
          rangeSets: {
            from: this._minRange || 0,
            to: this._maxRange || 0,
          },
        },
      ];
    } else {
      mappings = [
        {
          qualificationCode,
          conditionCodes: [''],
        },
      ];
    }
    let decipherCondition = '';
    if (this.mappingData.decipherQuestionId) {
      if (index) {
        decipherCondition = this.mappingData.decipherQuestionId + '.r' + index;
      } else if (
        this.isRangeType(this.qualificationDetails[0].qualification_code)
      ) {
        decipherCondition =
          this.mappingData.decipherQuestionId +
          '.ival in range' +
          (this._minRange || 0) +
          ',' +
          (this._maxRange || 0);
      }
    }
    const emptyTarget = {
      desc: '',
      mappings,
      decipherCode: '',
      decipherCondition,
    };
    (this.modalForm.get('target') as UntypedFormArray).push(
      this._generateTargetForm(emptyTarget)
    );
  };

  public removeConditionMapping = (index: number) => {
    (this.modalForm.get('target') as UntypedFormArray).removeAt(index);
  };

  public updateRange = (targetMapping: UntypedFormGroup, $event: Event) => {
    const newValue = (<HTMLInputElement>$event.target).value;
    const splitRange = typeof newValue == 'string' ? newValue.split('-') : [];
    const rangeSetsFG = targetMapping.get('rangeSets') as UntypedFormGroup;
    if (newValue === '') {
      rangeSetsFG.get('from')?.reset(null);
      rangeSetsFG.get('to')?.reset(null);
    } else if (this._isInvalidRangeInput(newValue)) {
      rangeSetsFG.get('from')?.setValue(newValue);
      rangeSetsFG.get('to')?.setValue(newValue);
    } else {
      rangeSetsFG.get('from')?.setValue(Number(splitRange[0]));
      rangeSetsFG.get('to')?.setValue(Number(splitRange[1]));
    }
  };

  private _isInvalidRangeInput = (value: string) => {
    const splitRange = typeof value == 'string' ? value.split('-') : [];
    return (
      splitRange.length != 2 ||
      isNaN(Number(splitRange[0])) ||
      isNaN(Number(splitRange[1]))
    );
  };

  public removeCondition = (
    targetMapping: UntypedFormGroup,
    coniditonIndex: number
  ) => {
    const conditionCodes = targetMapping.get(
      'conditionCodes'
    ) as UntypedFormArray;
    conditionCodes.removeAt(coniditonIndex);
    if (!conditionCodes.length) {
      conditionCodes.push(this._generateConditionControl());
    }
  };

  public optionLabel = (qualCode: number, condCode: string) => {
    let label = this.getQualificationAnswers(qualCode).filter((ans) => {
      return ans.condition_code === parseInt(condCode);
    });
    return label.length ? label[0]?.local_text : '';
  };

  public setCondition = (
    mapping: UntypedFormGroup,
    conditionIdx: number,
    selectedItem: QualificationData
  ) => {
    this.getConditionControls(mapping)[conditionIdx].setValue(
      selectedItem.condition_code.toString()
    );
  };

  private _patchFormData = (data: QuestionMapping) => {
    const targetArray = this.modalForm.get('target') as UntypedFormArray;
    data.target.forEach((target: ProcessedTarget) => {
      const targetGroup = this._generateTargetForm(target);
      this.addFillerMappingControl(
        targetGroup.get('mappings') as UntypedFormArray
      );
      this.getMappingFormControls(targetGroup).forEach(
        (mappingFG: UntypedFormGroup) => {
          this.addFillerConditionControl(
            mappingFG.get('conditionCodes') as UntypedFormArray
          );
        }
      );
      targetArray.push(targetGroup);
    });
  };

  addFillerConditionControl = (conditionCodesFormArray: UntypedFormArray) => {
    if (conditionCodesFormArray && !conditionCodesFormArray.length) {
      conditionCodesFormArray.push(this._generateConditionControl());
    }
  };

  addFillerMappingControl = (mappingFormArray: UntypedFormArray) => {
    if (mappingFormArray && !mappingFormArray.length) {
      mappingFormArray.push(this._generateMappingsForm());
    }
  };

  returnRangeString = (rangeObject: RangeSet) => {
    const { from, to } = rangeObject;
    return typeof from == 'number' && typeof to == 'number'
      ? `${from}-${to}`
      : '';
  };

  public setEmptyModal = () => {
    if (this.isRangeType(this.qualificationDetails[0].qualification_code)) {
      this.addConditionMapping(this.qualificationDetails[0].qualification_code);
    } else {
      this.qualificationDetails[0].localizations[0].answers.forEach(
        (ans, i) => {
          this.addConditionMapping(
            this.qualificationDetails[0].qualification_code,
            i + 1
          );
        }
      );
    }
  };

  public returnPlaceholder = (qCode: number) => {
    const { max, min } =
      this.getQualificationDetail(qCode)!.localizations[0]?.format;
    const { MAX, MIN } = DEFAULT_AGE_QUOTA_CRITERIA;
    const minString = `${typeof min === 'number' ? min : MIN}`;
    const maxString = `${typeof max === 'number' ? max : MAX}`;
    return `${minString} - ${maxString}`;
  };

  public isRangeType = (qCode: number) => {
    const qualification = this.getQualificationDetail(qCode);
    const { RANGE, INPUT } = QUALIFICATION_TYPES;
    return (
      qualification && ([RANGE, INPUT] as number[]).includes(qualification.type)
    );
  };

  ngOnInit(): void {
    if (this.mappingData.target && this.mappingData.target.length) {
      this._patchFormData(this.mappingData);
    } else {
      this.setEmptyModal();
    }
  }
}
